import { useEffect, useRef } from 'react';
import { usePersistenceStore } from '../store/persistenceStore';
import { useDialogueStore } from '../store/dialogueStore';
import { useToast } from '../contexts/ToastContext';
import { GraphData } from '../types/persistence';

export const useAutoSave = (interval: number = 30000) => {
  const { isDirty, saveGraph, currentGraph, isAuthenticated } = usePersistenceStore();
  const { nodes, edges, dialogueData } = useDialogueStore();
  const { toast } = useToast();
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSaveRef = useRef<Date | null>(null);

  useEffect(() => {
    if (!isDirty || !currentGraph || !isAuthenticated) {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      return;
    }

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Set new timeout
    saveTimeoutRef.current = setTimeout(async () => {
      try {
        const graphData: GraphData = {
          nodes,
          edges,
          metadata: dialogueData || undefined
        };
        await saveGraph(graphData, 'Auto-save');
        lastSaveRef.current = new Date();
        toast({
          title: 'Auto-saved',
          description: 'Your changes have been saved automatically',
          variant: 'success'
        });
      } catch (error) {
        toast({
          title: 'Auto-save failed',
          description: error instanceof Error ? error.message : 'Failed to save changes',
          variant: 'error'
        });
      }
    }, interval);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [isDirty, currentGraph, isAuthenticated, interval, saveGraph, toast, nodes, edges, dialogueData]);

  // Save on unmount if dirty
  useEffect(() => {
    return () => {
      if (isDirty && currentGraph && isAuthenticated) {
        // Attempt synchronous save on unmount
        const graphData: GraphData = {
          nodes,
          edges,
          metadata: dialogueData || undefined
        };
        saveGraph(graphData, 'Auto-save on close').catch(console.error);
      }
    };
  }, [isDirty, currentGraph, isAuthenticated, saveGraph, nodes, edges, dialogueData]);

  return {
    lastSave: lastSaveRef.current
  };
};